import React, { useState, useMemo } from 'react';
import { 
  FileText, 
  Download, 
  Eye, 
  ShieldCheck, 
  Info, 
  Calendar, 
  FileImage, 
  Folder, 
  FolderOpen, 
  ChevronRight, 
  ArrowLeft,
  Search,
  Files
} from 'lucide-react';
import { siteData } from '../data.js';
import SEO from '../components/SEO.jsx';
import { useCmsCollection } from '../lib/cms.js';

// De 5 specificerede mapper
const FOLDERS = [
  {
    id: 'breve',
    name: 'Breve om/til TFDI',
    description: 'Officielle udtalelser, høringssvar og henvendelser til og fra ministerier og udvalg.',
    iconColor: 'bg-red-50 text-red-600 border-red-200',
    badgeColor: 'bg-red-50 text-red-700',
  },
  {
    id: 'aarsrapporter',
    name: 'Årsrapporter',
    description: 'Foreningens godkendte årsrapporter med overblik over aktiviteter og økonomi.',
    iconColor: 'bg-red-50 text-red-600 border-red-200',
    badgeColor: 'bg-red-50 text-red-700',
  },
  {
    id: 'vedtaegter',
    name: 'Vedtægter',
    description: 'Foreningens juridiske fundament, formål og gældende samt historiske vedtægter.',
    iconColor: 'bg-red-50 text-red-600 border-red-200',
    badgeColor: 'bg-red-50 text-red-700',
  },
  {
    id: 'generalforsamlinger',
    name: 'Generalforsamlinger og referater',
    description: 'Indkaldelser, dagsordener og godkendte referater fra ordinære og ekstraordinære generalforsamlinger.',
    iconColor: 'bg-red-50 text-red-600 border-red-200',
    badgeColor: 'bg-red-50 text-red-700',
  },
  {
    id: 'indsamlinger',
    name: 'Indsamlinger',
    description: 'Offentlige indsamlingsregnskaber, tilladelser og dokumentation for indsamlingsnævnet.',
    iconColor: 'bg-red-50 text-red-600 border-red-200',
    badgeColor: 'bg-red-50 text-red-700',
  },
];

// Automatisk kategorisering baseret på dokumentets titel og beskrivelse
function categorizeDocument(doc) {
  const text = `${doc.name || ''} ${doc.description || ''}`.toLowerCase();
  
  if (text.includes('indsamling')) {
    return 'indsamlinger';
  }
  if (text.includes('vedtægt') || text.includes('vedtaegt')) {
    return 'vedtaegter';
  }
  if (text.includes('årsrapport') || text.includes('aarsrapport')) {
    return 'aarsrapporter';
  }
  if (text.includes('generalforsamling') || text.includes('referat') || text.includes('indkaldelse')) {
    return 'generalforsamlinger';
  }
  if (text.includes('brev') || text.includes('udenrigsministeriet') || text.includes('sundhedsudvalg') || text.includes('udtalelse')) {
    return 'breve';
  }
  // Standard fallback
  return 'breve';
}

export default function Documents() {
  const { data: rawDocs } = useCmsCollection('dokumenter', siteData.documents || []);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const documentsList = useMemo(() => {
    return rawDocs.map((d) => {
      const item = d.data || d;
      const doc = {
        name: item.titel || item.name,
        description: item.beskrivelse || item.description,
        type: item.type_tekst || item.type || 'PDF',
        size: item.stoerrelse || item.size,
        url: item.fil?.[0] || item.fil_url || item.url,
      };
      return {
        ...doc,
        folderId: categorizeDocument(doc),
      };
    });
  }, [rawDocs]);

  // Folder counts
  const folderCounts = useMemo(() => {
    const counts = {};
    FOLDERS.forEach((f) => {
      counts[f.id] = documentsList.filter((d) => d.folderId === f.id).length;
    });
    return counts;
  }, [documentsList]);

  // Filtrerede dokumenter
  const filteredDocuments = useMemo(() => {
    if (!activeFolderId && !searchQuery.trim()) {
      return [];
    }
    return documentsList.filter((doc) => {
      if (activeFolderId && doc.folderId !== activeFolderId) {
        return false;
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesName = (doc.name || '').toLowerCase().includes(query);
        const matchesDesc = (doc.description || '').toLowerCase().includes(query);
        if (!matchesName && !matchesDesc) return false;
      }
      return true;
    });
  }, [documentsList, activeFolderId, searchQuery]);

  const currentFolder = FOLDERS.find((f) => f.id === activeFolderId);

  return (
    <div className="pt-40 md:pt-44 pb-24 bg-slate-50 min-h-screen">
      <SEO
        title="Dokumenter & Regnskab | Gennemsigtighed og vedtægter"
        description="Find officielle dokumenter, regnskaber, indsamlingsgodkendelser og vedtægter for Tak For Din Indsats. Vi prioriterer 100% åbenhed og ansvarlighed."
      />
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 md:pt-6 mb-12">
        <div className="text-center max-w-3xl mx-auto">
          <span className="px-4 py-1.5 rounded-full text-xs font-bold bg-red-50 text-red-600 uppercase tracking-wider border border-red-100">
            Gennemsigtighed & Struktur
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 mt-4 mb-6 tracking-tight leading-tight">
            Dokumenter og <span className="text-red-600">regnskab</span>
          </h1>
          <p className="text-slate-600 text-lg leading-relaxed">
            Vælg en mappe nedenfor for at tilgå de tilhørende dokumenter, godkendte regnskaber og indsamlinger for Tak For Din Indsats.
          </p>
        </div>
      </div>

      {/* Mappe-overblik / Mapper */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              Vælg en mappe
            </h2>
            <p className="text-sm text-slate-500">
              Klik på en af mapperne for at se dokumenterne i mappen.
            </p>
          </div>

          {/* Søgning */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søg i alle dokumenter..."
              className="w-full pl-10 pr-4 py-2 bg-white rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all shadow-xs"
            />
          </div>
        </div>

        {/* Mapper Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {FOLDERS.map((folder) => {
            const isSelected = activeFolderId === folder.id;
            const count = folderCounts[folder.id] || 0;

            return (
              <button
                key={folder.id}
                onClick={() => {
                  setActiveFolderId(folder.id);
                  setSearchQuery('');
                }}
                className={`text-left p-5 rounded-2xl border transition-all duration-200 relative group flex flex-col justify-between ${
                  isSelected
                    ? 'bg-white border-red-500 shadow-lg shadow-red-500/10 ring-2 ring-red-500/20'
                    : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md shadow-xs'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className={`p-2.5 rounded-xl border ${folder.iconColor} transition-transform group-hover:scale-105`}>
                      {isSelected ? <FolderOpen size={22} /> : <Folder size={22} />}
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${folder.badgeColor}`}>
                      {count} dok.
                    </span>
                  </div>
                  <h3 className={`text-sm font-bold mb-1 transition-colors ${isSelected ? 'text-red-600' : 'text-slate-900 group-hover:text-red-600'}`}>
                    {folder.name}
                  </h3>
                  <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                    {folder.description}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-semibold">
                  <span className={isSelected ? 'text-red-600 font-bold' : 'text-slate-500 group-hover:text-slate-700'}>
                    {isSelected ? 'Mappe åben' : 'Åbn mappe'}
                  </span>
                  <ChevronRight size={14} className={`transition-transform ${isSelected ? 'rotate-90 text-red-600' : 'text-slate-400 group-hover:translate-x-0.5'}`} />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Aktiv Mappe Bar / Brødkrumme hvis valgt */}
      {currentFolder && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 px-6 flex flex-wrap items-center justify-between gap-4 shadow-xs">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setActiveFolderId(null)}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-red-600 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                <ArrowLeft size={14} />
                Luk mappe
              </button>
              <div className="h-4 w-px bg-slate-200 hidden sm:block"></div>
              <div className="flex items-center gap-2 text-sm">
                <FolderOpen className="text-red-600" size={18} />
                <span className="font-bold text-slate-900">{currentFolder.name}</span>
                <span className="text-slate-400 text-xs">({filteredDocuments.length} dokumenter)</span>
              </div>
            </div>
            <p className="text-xs text-slate-500 italic max-w-md">
              {currentFolder.description}
            </p>
          </div>
        </div>
      )}

      {/* Søge-status bar hvis brugeren søger uden at have valgt en mappe */}
      {!currentFolder && searchQuery.trim() && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 px-6 flex items-center justify-between gap-4 shadow-xs">
            <div className="flex items-center gap-2 text-sm">
              <Search className="text-red-600" size={16} />
              <span className="font-bold text-slate-900">Søgeresultater for "{searchQuery}"</span>
              <span className="text-slate-400 text-xs">({filteredDocuments.length} fundet)</span>
            </div>
            <button
              onClick={() => setSearchQuery('')}
              className="text-xs font-bold text-slate-600 hover:text-red-600 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              Ryd søgning
            </button>
          </div>
        </div>
      )}

      {/* Main Documents Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        {!activeFolderId && !searchQuery.trim() ? null : filteredDocuments.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center max-w-lg mx-auto shadow-xs">
            <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-4">
              <Files size={24} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              Ingen dokumenter fundet
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              {searchQuery ? `Der er ingen dokumenter, der matcher "${searchQuery}".` : 'Der er endnu ikke tilføjet dokumenter i denne mappe.'}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-900 text-white hover:bg-red-600 transition-colors"
              >
                Ryd søgning
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocuments.map((doc) => {
              const isPdf = doc.type.toLowerCase().includes('pdf');
              const folderObj = FOLDERS.find((f) => f.id === doc.folderId);
              
              return (
                <div 
                  key={doc.name} 
                  className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/40 p-7 flex flex-col justify-between hover:shadow-2xl hover:shadow-slate-200/60 transition-all duration-300 group"
                >
                  <div>
                    {/* Top badges: mappe og format */}
                    <div className="flex items-center justify-between mb-5">
                      <div className={`p-3.5 rounded-2xl ${isPdf ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'} transition-transform group-hover:scale-110 duration-300`}>
                        {isPdf ? <FileText size={24} /> : <FileImage size={24} />}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {folderObj && (
                          <span 
                            onClick={(e) => { e.stopPropagation(); setActiveFolderId(folderObj.id); }}
                            className="cursor-pointer text-[11px] font-semibold text-slate-500 hover:text-red-600 bg-slate-50 hover:bg-red-50 px-2.5 py-1 rounded-full border border-slate-100 transition-colors"
                            title={`Filter efter ${folderObj.name}`}
                          >
                            📁 {folderObj.name.split(' ')[0]}
                          </span>
                        )}
                        <span className="text-[11px] font-semibold text-slate-400 bg-slate-50 px-2.5 py-1 rounded-full border border-slate-100">
                          {doc.type} • {doc.size}
                        </span>
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-slate-900 mb-2.5 group-hover:text-red-600 transition-colors leading-snug">
                      {doc.name}
                    </h3>
                    
                    <p className="text-slate-600 text-sm leading-relaxed mb-6">
                      {doc.description}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2.5 pt-4 border-t border-slate-100">
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-slate-900 text-white hover:bg-red-600 transition-all duration-300 shadow-md shadow-slate-900/10 hover:shadow-red-600/10"
                    >
                      <Eye size={15} />
                      Vis dokument
                    </a>
                    <a
                      href={doc.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all duration-300"
                    >
                      <Download size={15} />
                      Hent
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Transparency Statement Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-900 text-white rounded-[2.5rem] p-8 md:p-12 shadow-xl relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600/5 rounded-full blur-3xl -ml-20 -mb-20"></div>

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 text-red-500 font-bold text-sm uppercase tracking-wider mb-4">
                <ShieldCheck size={18} />
                100% Frivilligt drevet
              </div>
              <h2 className="text-2xl md:text-3xl font-black mb-4 tracking-tight">
                Vores løfte om fuld gennemsigtighed
              </h2>
              <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                I Tak For Din Indsats er der ingen lønnede medarbejdere. Alle donationer, medlemskontingenter og bidrag går ubeskåret til at sikre en rettidig, helhedsorienteret og tværfaglig indsats for vores skadede veteraner, frontpersonale og deres familier. Vi offentliggør løbende vores godkendte regnskaber, så du altid kan følge med i, hvordan midlerne gør en forskel.
              </p>
            </div>
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <Info className="text-red-500 shrink-0 mt-0.5" size={18} />
                <div className="text-xs text-slate-300 leading-relaxed">
                  Har du spørgsmål til vores regnskab eller administration? Du er altid velkommen til at kontakte os direkte.
                </div>
              </div>
              <a 
                href="mailto:kontakt@takfordinindsats.dk"
                className="text-center block w-full py-2.5 rounded-xl text-xs font-bold bg-red-600 hover:bg-red-700 text-white transition-colors"
              >
                Kontakt administrationen
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
